import React, { useEffect, useState } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logoImage from "../../assets/logo-prima-hitam.png";
import formatRupiah from "utils/formatRupiah";
import moment from "moment";
import "moment/locale/id";

moment.locale("id");

// ======================================
//               STYLE
// ======================================
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9,
    fontFamily: "Helvetica",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #ccc",
    paddingBottom: 10,
    marginBottom: 14,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
  },
  logo: {
    width: 90,
    height: "auto",
  },
  periodText: {
    marginTop: 4,
    fontSize: 10,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottom: "1px solid #aaa",
    paddingVertical: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #ddd",
    paddingVertical: 4,
  },
  cellHeader: {
    fontWeight: "bold",
    paddingHorizontal: 4,
  },
  cell: {
    paddingHorizontal: 4,
    fontSize: 9,
  },

  footerTotal: {
    marginTop: 10,
    padding: 8,
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    textAlign: "right",
    borderTop: "1px solid #ccc",
  },
});

// ======================================
//      DOKUMEN PDF: LAPORAN GAJI
// ======================================
const MonthlyPayrollDocument = ({ data, columns, periode, cabang }) => {
  if (!data || !columns) {
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Text>Data tidak tersedia</Text>
        </Page>
      </Document>
    );
  }

  // hanya tampilkan kolom yang display !== false

  const visibleColumns = columns.filter((col) => {
    let isVisible =
      col.options?.display !== false && // hidden column
      col.name !== "aksi" &&
      col.name !== "id_karyawan"; // exclude column name "Aksi"

    // Tambahkan kondisi opsional jika terpenuhi
    if (cabang !== "all") {
      isVisible = isVisible && col.name !== "nama_cabang"; // contoh tambahan
    }

    return isVisible;
  });
  const totalGaji = data.reduce(
    (a, b) => a + (parseFloat(b.total_gaji) || 0),
    0
  );

  const columnFlex = visibleColumns.map((col) => {
    switch (col.name) {
      case "nama_karyawan":
        return 2; // lebih lebar untuk nama
      case "total_gaji":
        return 1.5;
      case "jumlah_hadir":
        return 0.5; // cukup lebar
      case "total_telat":
        return 0.5;
      default:
        return 1; // kolom kecil
    }
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* =========================== HEADER ============================ */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>LAPORAN GAJI KARYAWAN</Text>
            <Text style={styles.periodText}>
              Periode: {moment(periode).format("MMMM YYYY")}
            </Text>
            <Text style={styles.periodText}>
              Cabang: {cabang === "all" ? "Semua Cabang" : cabang}
            </Text>
          </View>

          <Image src={logoImage} style={styles.logo} />
        </View>

        {/* =========================== TABLE HEADER ============================ */}
        <View style={styles.tableHeader}>
          {visibleColumns.map((col, idx) => (
            <Text
              key={idx}
              style={[styles.cellHeader, { flex: columnFlex[idx] }]}
            >
              {col.label}
            </Text>
          ))}
        </View>

        {/* =========================== TABLE BODY ============================ */}
        {data.map((row, rowIndex) => (
          <View key={rowIndex} style={styles.tableRow}>
            {visibleColumns.map((col, colIndex) => {
              let value = row[col.name];
              if (col.options?.customBodyRender) {
                value = col.options.customBodyRender(value);
              }
              if (colIndex === 1 && typeof value === "string") {
                const parts = value.split(" - ");
                value = parts[1] ?? value; // fallback jika tidak ada index 1
              }
              return (
                <Text
                  key={colIndex}
                  style={[styles.cell, { flex: columnFlex[colIndex] }]}
                >
                  {colIndex > 3 ? formatRupiah(parseFloat(value)) : value}
                </Text>
              );
            })}
          </View>
        ))}

        {/* =========================== FOOTER TOTAL ============================ */}
        <Text style={styles.footerTotal}>
          Total Gaji : {formatRupiah(totalGaji)}
        </Text>
      </Page>
    </Document>
  );
};

// ======================================
//       VIEWER HALAMAN REACT
// ======================================
const CetakLaporanGajiBulanan = () => {
  const [list, setList] = useState(null);
  const [columns, setColumns] = useState(null);
  const [periode, setPeriode] = useState(null);
  const [cabang, setCabang] = useState(null);

  useEffect(() => {
    const d = sessionStorage.getItem("datalaporangaji");
    const c = sessionStorage.getItem("columnsSetoran");
    const p = sessionStorage.getItem("selectedPeriod");
    const u = sessionStorage.getItem("selectedCabang");

    if (d) setList(JSON.parse(d));
    if (c) setColumns(JSON.parse(c));
    if (p) setPeriode(JSON.parse(p));
    if (u) setCabang(JSON.parse(u));
  }, []);

  return (
    <div style={{ height: "100vh" }}>
      <PDFViewer width="100%" height="100%">
        <MonthlyPayrollDocument
          data={list}
          columns={columns}
          periode={periode}
          cabang={cabang}
        />
      </PDFViewer>
    </div>
  );
};

export default CetakLaporanGajiBulanan;
